// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

// vue 3 version
import { Router } from "vue-router";
import { ref, reactive, computed } from "vue";
import { Log, User, UserManager, Profile, WebStorageStateStore, UserManagerEvents } from "oidc-client";
import { OidcSettings } from "../config/oidcSettings";

const location = window.location;
const appRootUrl = `${location.protocol}//${location.host}${process.env.BASE_URL}`;

/**
 * A wrapper on oidc-client with vue support.
 */
export interface OidcManager {
  /**
   * Original app url used to create this instance.
   */
  readonly appRootUrl: string;

  /**
   * Gets whether the user is authenticated.
   */
  readonly isAuthenticated: boolean;

  /**
   * Gets the API access token if applicable.
   */
  readonly accessToken: string;

  /**
   * Gets the user claims if authenticated.
   */
  readonly userProfile: Profile;

  /**
   * Gets the auth events provided by oidc-client.
   */
  readonly events: UserManagerEvents;

  /**
   * Required call before all the properties are reliably initialized.
   * Should be called and waited on before starting the root Vue instance.
   */
  init(oidcSettings: OidcSettings, router?: Router): Promise<boolean>;

  /**
   * Required call before all the properties are reliably initialized.
   * Should be called and waited on before starting the root Vue instance.
   */
  reset(oidcSettings: OidcSettings, router?: Router): Promise<boolean>;

  /**
   * Hookup this auth instance with a vue-router instance.
   * This will guard routes with meta: { authName: `name of this auth` }
   * and register redirect callback routes.
   * @param router - the vue router instance.
   */
  initRouter(router: Router): void;

  /**
   * Starts the login flow explicitly.
   * @param args
   */
  signIn(args?: any): Promise<User | void>;

  /**
   * Revokes the tokens and removes the stored user.
   */
  signOut(): Promise<void>;

  /**
   * Enables silent renew.
   */
  startSilentRenew(): void;

  /**
   * Processes silent sign in callback.
   */
  processSilentSignInCallback(): Promise<User | undefined>;

  /**
   * Disables silent renew.
   */
  stopSilentRenew(): void;

  /**
   * Manages callback of the redirect signin
   */
  signinRedirectCallback(): Promise<User>;

  /**
   * Stores user
   * @param user - the user to store
   */
  storeUser(user: User): Promise<void>;
}

/**
 * Creates an openid-connect manager instance.
 */
function createOidcManager(): OidcManager {
  Log.logger = console;
  let mgr: UserManager;

  let _inited = false;
  const user = ref(null as User | null);

  const authObj = reactive({
    appRootUrl,
    isAuthenticated: computed(() => !!user.value && !user.value.expired),
    accessToken: computed(() => (!!user.value && !user.value.expired ? user.value.access_token : "")),
    userProfile: computed(() =>
      !!user.value && !user.value.expired
        ? user.value.profile
        : {
            iss: "",
            sub: "",
            aud: "",
            exp: 0,
            iat: 0,
          }
    ),
    events: computed(() => mgr.events),

    async init(oidcSettings: OidcSettings, router?: Router) {
      if (_inited) {
        return true;
      }

      const config = {
        userStore: new WebStorageStateStore({
          store: localStorage,
        }),
        ...oidcSettings,
      };

      mgr = new UserManager(config);

      this.startSilentRenew();

      if (router) {
        this.initRouter(router);
      }

      // load user from storage
      try {
        const data = await mgr.getUser();
        _inited = true;
        if (data && !data.expired) {
          user.value = data;
        }
        return true;
      } catch (err) {
        Log.warn(`Auth startup err = ${err}`);
        return false;
      }
    },

    reset(oidcSettings: OidcSettings, router?: Router) {
      _inited = false;
      return this.init(oidcSettings, router);
    },

    signIn(args?: any) {
      return signInReal(args);
    },

    async signOut() {
      try {
        await mgr.revokeAccessToken();
      } catch (err) {
        Log.warn(`Token revocation err = ${err}`);
      }
      await mgr.removeUser();
      user.value = null;
    },

    storeUser(user: User) {
      return mgr.storeUser(user);
    },

    processSilentSignInCallback() {
      return mgr.signinSilentCallback();
    },

    startSilentRenew() {
      mgr.startSilentRenew();
    },

    stopSilentRenew() {
      mgr.stopSilentRenew();
    },

    signinRedirectCallback() {
      return mgr.signinRedirectCallback();
    },

    initRouter(router: Router) {
      Log.debug("initRouter...");
      router.beforeEach(async (to, from, next) => {
        if (to.matched.some((record) => record.meta.isPublic !== true)) {
          Log.debug("Private route...");
          if (authObj.isAuthenticated) {
            await next();
          } else {
            await router.push({ name: "welcome" });
          }
        } else {
          Log.debug("Public route...");
          await next();
        }
      });
    },
  });

  function signInReal(args?: any) {
    return mgr.signinRedirect(args);
  }

  return authObj;
}

const oidcManager = createOidcManager();
export default oidcManager;
